/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useState } from "react";
import { Button } from "reactstrap";
import useConfig from "../../../../../actions/Tenants/config/configHook";
import Plot from "../../../../../plotly/Plot";
import { isFilledArray } from "../../../../../utils";
import { useQcEntities } from "../../QcEntityContext";

import { useRouteLayerQuery } from "../../../../../hooks/useCurrentEntities";

export default function SmartSamplingHistogram() {
    const { check, layer } = useQcEntities();
    const config = useConfig();
    const isLoading = useRouteLayerQuery().isLoading;
    const [isLogScale, setIsLogScale] = useState(true);


    if (isLoading || !check) {
        return null;
    }


    const fruit_type = config.get_fruit_type(layer.fruit_type);


    const the_field = fruit_type?.ripening_fields?.[0];


    if (!the_field?.fieldname_getter) {
        return null;
    }
    const histogram_key = `${the_field.category_name || "ripening_class"}_histogram`;
    const histogramStr = check?.[histogram_key];

    if (!histogramStr) {
        return null;
    }

    // convert json string
    let histogram: Array<[number, number, number]> = [];
    try {
        histogram = JSON.parse(histogramStr);
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("Invalid histogram JSON", histogramStr);
        return null;
    }

    if (!isFilledArray(histogram)) {
        return null;
    }
    const xLabels = histogram.map(([from, to]) => `${from.toFixed(1)}–${to.toFixed(1)}`);
    const xValues = histogram.map(([from, to]) => (from + to) / 2);
    const yValues = histogram.map(([, , count]) => count);
    const totalCount = yValues.reduce((sum, count) => sum + count, 0);
    const yPercentages = yValues.map((count) => (count / totalCount) * 100);

    const maxY = Math.max(...yPercentages);
    const range = [0, Math.ceil(Math.max(...histogram.map(([, to]: any) => to * 1.02)))];

    const histogramGraph = {
        type: "bar",
        x: xValues,
        y: yPercentages,
        name: `${the_field.label} Distribution`,
        marker: {
            color: "#0a230c",
            opacity: 1
        },
    };

    const layout: any = {
        autosize: true,
        showlegend: false,
        dragmode: false,
        margin: { l: 0, r: 0, t: 0, b: 50, pad: 0 },
        bargap: 0.005,
        xaxis: {
            fixedrange: false,
            title: the_field.label,
            type: isLogScale ? "log" : "linear",
            showGrid: false,
            range: isLogScale ? [0, Math.log10(range[1])] : range,
            tickvals: xValues,
            ticktext: xLabels,

        },
        yaxis: {
            range: [0, maxY * 1.1],
            showGrid: false,
            tickformat: ".0f%",
            visible: false,
        },
        shapes: [],
        annotations: [],
        plot_bgcolor: "#f9f9f9"
    };

    // Add swimming lanes or vertical category bands here
    if (isFilledArray(the_field.categories)) {

        // * add swimming lanes per category
        const categories = the_field.categories.map((i: any) => {
            const cat = ({
                ...i,
                label: i.label,
                lower: parseFloat(i.gt || i.gte || 0),
                upper: parseFloat(i.lt || i.lte || 100),
            });
            cat.log_upper = isLogScale ? Math.log10(cat.upper <= 0 ? 0.0001 : cat.upper) : cat.upper;
            cat.log_lower = isLogScale ? Math.log10(cat.lower <= 0 ? 0.0001 : cat.lower) : cat.lower;
            cat.middle = (cat.lower + cat.upper) / 2;
            cat.log_middle = (cat.log_lower + cat.log_upper) / 2;
            return cat;

        });

        const verticalBands = categories.map((category: any) => {
            return {
                type: "rect",
                y0: 0,
                y1: maxY * 1.1,
                x0: category.lower,
                x1: category.upper,
                fillcolor: category.color || "#D3D3D3",
                opacity: 0.07,
                line: {
                    width: 0
                },
                layer: "below",
            };
        });

        layout.shapes = verticalBands;
        layout.annotations = categories.map((category: any) => ({
            x: isLogScale ? category.log_middle : category.middle,
            y: maxY * 1.02,
            text: category.label,
            showarrow: false,
            font: {
                size: 12,
                color: "#4C4E52",
            },
            xanchor: "center",
            yanchor: "bottom",
        }));
    }

    return (
        <div className="py-4 border-bottom border-top">
            <div className="d-flex">
                <div>
                    <h5 className="mb-0">{`${the_field?.label} Distribution` }</h5>

                </div>
                <div className="ms-auto">
                    <Button
                        size="sm"
                        color="secondary"
                        outline={!isLogScale}
                        onClick={() => setIsLogScale((prev) => !prev)} // Toggle the scale
                    >Log scale</Button>
                </div>
            </div>
            <div css={css`min-height: 20rem;`} className="pt-2">
                <Plot
                    data={[histogramGraph as any]}
                    layout={layout as any}
                    useResizeHandler
                    style={{ width: "100%", height: "100%" }}
                    config={{ displayModeBar: false, displaylogo: false, staticPlot: true, scrollZoom: false, doubleClick: false, doubleClickDelay: 0 }}
                />
            </div>

        </div>
    );
}


